import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, RefreshCw, Mail, Phone, Building2, MessageCircle, ShieldCheck, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/adminApi";

const STATUS_OPTIONS = [
  { value: "new",                   label: "Nuevo",                 color: "#667eea", bg: "rgba(102,126,234,0.10)" },
  { value: "contacted",             label: "Contactado",            color: "#0ea5e9", bg: "rgba(14,165,233,0.10)" },
  { value: "pitching",              label: "Pitching",              color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  { value: "commercial_negotiation",label: "Neg. comercial",        color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { value: "contract_negotiation",  label: "Neg. de contrato",      color: "#d97706", bg: "rgba(217,119,6,0.12)" },
  { value: "won",                   label: "Ganado",                color: "#16a34a", bg: "rgba(22,163,74,0.12)" },
  { value: "lost",                  label: "Perdido",               color: "#dc2626", bg: "rgba(220,38,38,0.10)" },
  { value: "closed_not_fit",        label: "Cerrado · no encaja",   color: "#64748b", bg: "rgba(100,116,139,0.12)" },
  { value: "closed_other_reason",   label: "Cerrado · otro motivo", color: "#94a3b8", bg: "rgba(148,163,184,0.14)" }
];

const RESPONSIBLE_OPTIONS = [
  { value: "",       label: "Sin asignar" },
  { value: "arpit",  label: "Arpit" },
  { value: "naveen", label: "Naveen" },
  { value: "daniel", label: "Daniel" }
];

const SOURCE_LABELS = {
  "/": "Home",
  "/producto": "Product",
  "/stablecoin": "Stablecoin",
  "/ai-agents": "AI Agents",
  "/casos-de-uso": "Use Cases",
  "/integracion": "Integration",
  "/seguridad": "Security",
  "/contacto": "Contact (direct)"
};

const formatSource = (s) => {
  const p = (s?.source_path || "").trim();
  if (!p) return "Direct";
  return SOURCE_LABELS[p] || p;
};

const formatReferrer = (s) => {
  const r = (s?.referrer || "").trim();
  if (!r) return "";
  try { return new URL(r).hostname; } catch { return r; }
};

const STATUS_META = STATUS_OPTIONS.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [responsibleFilter, setResponsibleFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [activeSubmission, setActiveSubmission] = useState(null);
  const callbackHandled = useRef(false);

  // ---- AuthCallback: read #session_id, exchange it, then clean URL ----
  const handleAuthCallback = useCallback(async (sessionId) => {
    try {
      const { data } = await api.post("/api/auth/google/session", { session_id: sessionId });
      if (data?.session_token) {
        localStorage.setItem("admin_session_token", data.session_token);
      }
      setUser(data.user);
      // strip the hash so a refresh doesn't reuse the session_id
      window.history.replaceState(null, "", "/admin");
      return data.user;
    } catch (err) {
      const detail = err?.response?.data?.detail || "No tienes acceso al panel administrativo.";
      toast.error(detail);
      navigate("/admin/login?err=forbidden", { replace: true });
      return null;
    }
  }, [navigate]);

  // ---- /auth/me check on mount ----
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // 1) Handle the auth callback hash synchronously
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      if (match && !callbackHandled.current) {
        callbackHandled.current = true;
        const u = await handleAuthCallback(match[1]);
        if (!u) return;
        if (!cancelled) await fetchSubmissions();
        return;
      }
      // 2) Otherwise verify the existing session
      try {
        const { data } = await api.get("/api/auth/me");
        if (cancelled) return;
        setUser(data);
        await fetchSubmissions();
      } catch {
        if (!cancelled) navigate("/admin/login", { replace: true });
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/contact");
      setSubmissions(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate("/admin/login", { replace: true });
        return;
      }
      toast.error("No pudimos cargar los leads.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const updateLead = useCallback(async (id, patch) => {
    try {
      const { data } = await api.patch(`/api/admin/contact/${id}`, patch);
      setSubmissions((prev) => prev.map((s) => (s.id === id ? { ...s, ...data } : s)));
      setActiveSubmission(activeSubmission?.id === id ? { ...activeSubmission, ...data } : activeSubmission);
      toast.success("Lead actualizado.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "No pudimos actualizar el lead.";
      toast.error(typeof detail === "string" ? detail : "Error al actualizar.");
    }
  }, [activeSubmission]);
  const updateStatus = (id, status) => updateLead(id, { status });
  const updateResponsible = (id, responsible) => updateLead(id, { responsible });

  const logout = async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      /* swallow */
    }
    localStorage.removeItem("admin_session_token");
    navigate("/admin/login", { replace: true });
  };

  // ---- Derived: filtered list + counts ----
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return submissions.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (responsibleFilter !== "all" && (s.responsible || "") !== responsibleFilter) return false;
      if (q) {
        const hay = `${s.name} ${s.email} ${s.company || ""} ${s.message}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [submissions, statusFilter, responsibleFilter, search]);

  const counts = useMemo(() => {
    const c = { all: submissions.length };
    STATUS_OPTIONS.forEach((s) => (c[s.value] = 0));
    submissions.forEach((s) => {
      if (c[s.status] !== undefined) c[s.status] += 1;
    });
    return c;
  }, [submissions]);

  if (!user) {
    return (
      <div style={styles.loadingPage} data-testid="admin-loading">
        <div style={styles.spinner} />
        <div style={{ marginTop: 16, color: "#475569" }}>Cargando panel…</div>
      </div>
    );
  }

  return (
    <div style={styles.page} data-testid="admin-page">
      {/* Top bar */}
      <div style={styles.topbar}>
        <div style={styles.brand}>
          <div style={styles.brandIcon}><ShieldCheck size={18} strokeWidth={2} /></div>
          <div>
            <div style={styles.brandTitle}>Tarjetas UnDosTres · Admin</div>
            <div style={styles.brandSub}>Panel de leads</div>
          </div>
        </div>
        <div style={styles.userBlock}>
          <div style={styles.userInfo}>
            <div style={styles.userName}>{user.name || user.email}</div>
            <div style={styles.userEmail}>{user.email}</div>
          </div>
          <button
            type="button"
            onClick={logout}
            style={styles.logoutBtn}
            data-testid="admin-logout-button"
          >
            <LogOut size={16} strokeWidth={2} />
            <span>Salir</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filtersBar}>
        <div style={styles.statusFilters} data-testid="admin-status-filters">
          {[{ value: "all", label: "Todos" }, ...STATUS_OPTIONS].map((opt) => {
            const active = statusFilter === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                style={{
                  ...styles.filterChip,
                  ...(active ? styles.filterChipActive : {})
                }}
                data-testid={`admin-filter-${opt.value}`}
              >
                <span>{opt.label}</span>
                <span style={styles.filterCount}>{counts[opt.value] ?? 0}</span>
              </button>
            );
          })}
        </div>
        <div style={styles.rightControls}>
          <input
            type="search"
            placeholder="Buscar por nombre, email o empresa…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
            data-testid="admin-search-input"
          />
          <button
            type="button"
            onClick={fetchSubmissions}
            style={styles.refreshBtn}
            data-testid="admin-refresh-button"
            aria-label="Refrescar"
          >
            <RefreshCw size={16} strokeWidth={2} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.responsibleBar} data-testid="admin-responsible-filters">
        <span style={styles.responsibleBarLabel}>Responsable:</span>
        {[{ value: "all", label: "Todos" }, ...RESPONSIBLE_OPTIONS].map((opt) => {
          const active = responsibleFilter === opt.value;
          return (
            <button
              key={`r-${opt.value}`}
              type="button"
              onClick={() => setResponsibleFilter(opt.value)}
              style={{ ...styles.filterChip, ...(active ? styles.filterChipActive : {}) }}
              data-testid={`admin-responsible-filter-${opt.value || "unassigned"}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      <div style={styles.tableWrap}>
        <table style={styles.table} data-testid="admin-submissions-table">
          <thead>
            <tr>
              <th style={styles.th}>Lead</th>
              <th style={styles.th}>Empresa</th>
              <th style={styles.th}>Recibido</th>
              <th style={styles.th}>Responsable</th>
              <th style={styles.th}>Estado</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={styles.tdEmpty}>Cargando…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={styles.tdEmpty}>Sin leads para mostrar.</td></tr>
            ) : (
              filtered.map((s) => {
                const meta = STATUS_META[s.status] || STATUS_META.new;
                return (
                  <tr key={s.id} style={styles.row} data-testid={`admin-row-${s.id}`}>
                    <td style={styles.td}>
                      <div style={styles.leadName}>{s.name}</div>
                      <a href={`mailto:${s.email}`} style={styles.leadEmail}>{s.email}</a>
                      <div style={styles.sourceChip} data-testid={`admin-source-${s.id}`}>
                        <Globe size={11} /> {formatSource(s)}
                      </div>
                    </td>
                    <td style={styles.td}>{s.company || "—"}</td>
                    <td style={styles.td}>{formatDate(s.created_at)}</td>
                    <td style={styles.td}>
                      <select
                        value={s.responsible || ""}
                        onChange={(e) => updateResponsible(s.id, e.target.value)}
                        style={styles.responsibleSelect}
                        data-testid={`admin-responsible-select-${s.id}`}
                      >
                        {RESPONSIBLE_OPTIONS.map((o) => (
                          <option key={o.value || "none"} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <select
                        value={s.status}
                        onChange={(e) => updateStatus(s.id, e.target.value)}
                        style={{
                          ...styles.statusSelect,
                          color: meta.color,
                          background: meta.bg,
                          borderColor: meta.color + "40"
                        }}
                        data-testid={`admin-status-select-${s.id}`}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={styles.td}>
                      <button
                        type="button"
                        onClick={() => setActiveSubmission(s)}
                        style={styles.viewBtn}
                        data-testid={`admin-view-${s.id}`}
                      >
                        Ver mensaje
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {activeSubmission && (
        <div
          style={styles.drawerOverlay}
          onClick={() => setActiveSubmission(null)}
          data-testid="admin-detail-overlay"
        >
          <div
            style={styles.drawer}
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-detail-drawer"
          >
            <div style={styles.drawerHead}>
              <div>
                <div style={styles.drawerName}>{activeSubmission.name}</div>
                <a href={`mailto:${activeSubmission.email}`} style={styles.drawerEmail}>
                  <Mail size={14} /> {activeSubmission.email}
                </a>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubmission(null)}
                style={styles.drawerClose}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>
            <div style={styles.drawerMeta}>
              <div style={styles.metaItem}>
                <Building2 size={14} /> {activeSubmission.company || "—"}
              </div>
              <div style={styles.metaItem}>
                <Globe size={14} />
                {activeSubmission.website ? (
                  <a
                    href={
                      activeSubmission.website.toLowerCase().startsWith("http")
                        ? activeSubmission.website
                        : `https://${activeSubmission.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.metaLink}
                    data-testid="admin-detail-website-link"
                  >
                    {activeSubmission.website}
                  </a>
                ) : (
                  "—"
                )}
              </div>
              <div style={styles.metaItem}>
                <Phone size={14} /> {activeSubmission.phone || "—"}
              </div>
              <div style={styles.metaItem}>
                <span style={styles.localeChip}>{activeSubmission.locale?.toUpperCase()}</span>
                {formatDate(activeSubmission.created_at)}
              </div>
              <div style={styles.metaItem} data-testid="admin-detail-source">
                <Globe size={14} />
                <span style={{ fontWeight: 600, color: "#1a202c" }}>{formatSource(activeSubmission)}</span>
                {formatReferrer(activeSubmission) && (
                  <span style={{ fontSize: 12, color: "#64748b" }}>· via {formatReferrer(activeSubmission)}</span>
                )}
              </div>
            </div>
            <div style={styles.messageBox}>
              <div style={styles.messageLabel}><MessageCircle size={14} /> Mensaje</div>
              <div style={styles.messageBody}>{activeSubmission.message}</div>
            </div>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>Responsable:</span>
              <select
                value={activeSubmission.responsible || ""}
                onChange={(e) => updateResponsible(activeSubmission.id, e.target.value)}
                style={styles.statusSelectLg}
                data-testid="admin-detail-responsible-select"
              >
                {RESPONSIBLE_OPTIONS.map((o) => (
                  <option key={o.value || "none"} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div style={styles.statusRow}>
              <span style={styles.statusLabel}>Estado:</span>
              <select
                value={activeSubmission.status}
                onChange={(e) => updateStatus(activeSubmission.id, e.target.value)}
                style={styles.statusSelectLg}
                data-testid="admin-detail-status-select"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {activeSubmission.updated_at && (
              <div style={styles.updatedNote}>
                Actualizado por {activeSubmission.updated_by || "—"} el {formatDate(activeSubmission.updated_at)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: { minHeight: "100vh", background: "#f7f7fb", padding: "24px 32px 80px" },
  loadingPage: { minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#f7f7fb" },
  spinner: { width: 32, height: 32, border: "3px solid rgba(102,126,234,0.20)", borderTopColor: "#667eea", borderRadius: "50%", animation: "admin-spin 0.9s linear infinite" },
  topbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", background: "#fff", borderRadius: "16px", border: "1px solid rgba(0,0,0,0.05)", marginBottom: 20, boxShadow: "0 2px 8px rgba(15,23,42,0.03)" },
  brand: { display: "flex", alignItems: "center", gap: 14 },
  brandIcon: { width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" },
  brandTitle: { fontWeight: 700, color: "#0f172a", fontSize: 16, letterSpacing: "-0.01em" },
  brandSub: { fontSize: 12, color: "#64748b" },
  userBlock: { display: "flex", alignItems: "center", gap: 16 },
  userInfo: { textAlign: "right" },
  userName: { fontWeight: 600, color: "#0f172a", fontSize: 14 },
  userEmail: { fontSize: 12, color: "#64748b" },
  logoutBtn: { display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: "#fff", border: "1px solid rgba(0,0,0,0.10)", borderRadius: 10, color: "#475569", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  filtersBar: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, marginBottom: 16, flexWrap: "wrap" },
  statusFilters: { display: "flex", gap: 8, flexWrap: "wrap" },
  filterChip: { display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", background: "#fff", border: "1px solid rgba(0,0,0,0.08)", borderRadius: 999, color: "#475569", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  filterChipActive: { background: "linear-gradient(135deg,#667eea,#764ba2)", color: "#fff", borderColor: "transparent" },
  filterCount: { fontSize: 11, padding: "2px 7px", background: "rgba(0,0,0,0.06)", borderRadius: 999, fontWeight: 700 },
  rightControls: { display: "flex", gap: 10, alignItems: "center" },
  responsibleBar: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 14, paddingBottom: 4 },
  responsibleBarLabel: { fontSize: 12, fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase", marginRight: 4 },
  responsibleSelect: { padding: "7px 12px", borderRadius: 8, border: "1px solid rgba(0,0,0,0.10)", fontSize: 12.5, fontWeight: 600, cursor: "pointer", outline: "none", appearance: "none", paddingRight: 28, color: "#1a202c", background: "#ffffff", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%2364748b' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" },
  searchInput: { padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.10)", fontSize: 13.5, width: 280, background: "#fff", outline: "none" },
  refreshBtn: { width: 40, height: 40, borderRadius: 10, border: "1px solid rgba(0,0,0,0.10)", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#475569" },
  tableWrap: { background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.05)", overflow: "hidden", boxShadow: "0 2px 8px rgba(15,23,42,0.03)" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "14px 18px", fontSize: 12, fontWeight: 700, color: "#64748b", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid rgba(0,0,0,0.06)", background: "#fafbff" },
  row: { transition: "background 0.15s" },
  td: { padding: "14px 18px", fontSize: 14, color: "#1a202c", borderBottom: "1px solid rgba(0,0,0,0.05)", verticalAlign: "middle" },
  tdEmpty: { padding: "32px 18px", textAlign: "center", color: "#64748b", fontSize: 14 },
  leadName: { fontWeight: 600 },
  leadEmail: { fontSize: 12, color: "#667eea", textDecoration: "none" },
  sourceChip: { display: "inline-flex", alignItems: "center", gap: 4, marginTop: 6, padding: "2px 8px", background: "rgba(245,87,108,0.10)", color: "#c53b58", borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: "0.02em" },
  localeChip: { fontSize: 11, padding: "3px 8px", background: "rgba(102,126,234,0.10)", color: "#5848c7", borderRadius: 999, fontWeight: 700, marginRight: 8 },
  statusSelect: { padding: "7px 12px", borderRadius: 8, border: "1px solid", fontSize: 12.5, fontWeight: 700, cursor: "pointer", outline: "none", appearance: "none", paddingRight: 28, backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%2364748b' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" },
  viewBtn: { background: "transparent", border: "none", color: "#667eea", fontWeight: 600, fontSize: 13, cursor: "pointer", padding: 0 },
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.40)", backdropFilter: "blur(4px)", display: "flex", justifyContent: "flex-end", zIndex: 1100 },
  drawer: { width: "min(520px, 100%)", background: "#fff", height: "100%", padding: "28px 32px", overflowY: "auto", boxShadow: "-20px 0 60px rgba(15,23,42,0.18)" },
  drawerHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 20 },
  drawerName: { fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em", color: "#0f172a" },
  drawerEmail: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "#667eea", textDecoration: "none", marginTop: 4 },
  drawerClose: { background: "transparent", border: "none", fontSize: 28, color: "#94a3b8", cursor: "pointer", lineHeight: 1, padding: 0 },
  drawerMeta: { display: "flex", flexWrap: "wrap", gap: 14, fontSize: 13, color: "#475569", marginBottom: 20 },
  metaItem: { display: "inline-flex", alignItems: "center", gap: 6 },
  metaLink: { color: "#667eea", textDecoration: "none", fontWeight: 500 },
  messageBox: { background: "#f8fafc", borderLeft: "3px solid #667eea", padding: "16px 18px", borderRadius: 8, marginBottom: 24 },
  messageLabel: { display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "#64748b", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 },
  messageBody: { fontSize: 14.5, lineHeight: 1.6, color: "#1a202c", whiteSpace: "pre-wrap" },
  statusRow: { display: "flex", alignItems: "center", gap: 12, marginTop: 8 },
  statusLabel: { fontSize: 13, color: "#475569", fontWeight: 600 },
  statusSelectLg: { padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(0,0,0,0.10)", fontSize: 13.5, fontWeight: 700, cursor: "pointer", outline: "none", background: "#fff" },
  updatedNote: { marginTop: 18, fontSize: 12, color: "#94a3b8" }
};

// One-time keyframes injection.
if (typeof document !== "undefined" && !document.getElementById("admin-spin-keyframes")) {
  const el = document.createElement("style");
  el.id = "admin-spin-keyframes";
  el.textContent = "@keyframes admin-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(el);
}

export default AdminPage;
